import { useCallback, useEffect, useState } from "react";
import { IJiraSearchResult } from "../../api/IJiraSearchResult";
import { IJiraSprint } from "../../api/IJiraSprint";
import { useMeetingStore } from "../../stores/useMeetingStore";
import { useUserStore } from "../../stores/useUserStore";
import useAxios from "../api/useAxios";
import useShowError from "../utils/useShowError";
import IMeeting from "../../api/IMeeting";

const useBatchAddJira = (onClose: () => void) => {
    const meeting = useMeetingStore((state) => state.meeting);
    const setMeeting = useMeetingStore((state) => state.setMeeting);
    const user = useUserStore((state) => state.user);

    const { error, loading, setError, clearError, post } = useAxios();
    useShowError(error, clearError);

    const {
        error: searchError,
        loading: searchLoading,
        setError: setSearchError,
        clearError: clearSearchError,
        post: searchPost,
    } = useAxios();
    useShowError(searchError, clearSearchError);

    const {
        error: sprintError,
        loading: sprintsLoading,
        setError: setSprintError,
        clearError: clearSprintError,
        post: sprintPost,
    } = useAxios();
    useShowError(sprintError, clearSprintError);

    const [sprints, setSprints] = useState<IJiraSprint[]>([]);
    const [selectedSprint, setSelectedSprint] = useState("");
    const [searchResults, setSearchResults] = useState<IJiraSearchResult[]>([]);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const missingJiraToken = !user?.jiraEmail || !user?.jiraToken;

    useEffect(() => {
        if (!missingJiraToken) return;

        setSprints([]);
        setSelectedSprint("");
        setSearchResults([]);
        setSelectedKeys(new Set());
    }, [missingJiraToken]);

    // Fetch sprints on mount
    useEffect(() => {
        if (!meeting || !user) return;

        if (missingJiraToken) {
            setSprints([]);
            return;
        }

        const fetchSprints = async () => {
            const authValues = {
                meetingId: meeting.id,
                email: user.jiraEmail,
                token: user.jiraToken,
            };
            const res = await sprintPost<IJiraSprint[]>(
                "/api/meeting/GetJiraSprints",
                authValues
            );
            if (res) setSprints(res);
        };
        fetchSprints();
    }, [meeting?.id, user?.jiraEmail, user?.jiraToken, missingJiraToken]);

    // Auto-search when sprint selection changes
    useEffect(() => {
        if (selectedSprint) {
            searchBySprint();
        } else {
            setSearchResults([]);
            setSelectedKeys(new Set());
        }
    }, [selectedSprint]);

    const searchBySprint = useCallback(async () => {
        if (!meeting) return setSearchError("Missing Meeting");
        if (!user) return setSearchError("Missing User");
        if (!selectedSprint) return;

        if (missingJiraToken) {
            setSearchResults([]);
            setSelectedKeys(new Set());
            return;
        }

        const authValues = {
            meetingId: meeting.id,
            email: user.jiraEmail,
            token: user.jiraToken,
        };

        const url = `/api/meeting/SearchJiraIssues?sprintId=${selectedSprint}`;
        const res = await searchPost<IJiraSearchResult[]>(url, authValues);
        if (res) {
            const existingKeys = new Set(
                meeting.subTopics
                    ?.flatMap((st) => [
                        st.jiraKey,
                        ...st.tasks.map((t) => t.jiraKey),
                    ])
                    .filter(Boolean) ?? []
            );
            const filtered = res.filter((r) => !existingKeys.has(r.key));
            setSearchResults(filtered);
            setSelectedKeys(new Set());
        }
    }, [meeting, user, selectedSprint, searchPost, setSearchError]);

    const toggleSelect = useCallback((key: string) => {
        setSelectedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedKeys((prev) => {
            if (prev.size === searchResults.length) {
                return new Set();
            }
            return new Set(searchResults.map((r) => r.key));
        });
    }, [searchResults]);

    const submit = useCallback(async () => {
        if (!meeting) return setError("Missing Meeting");
        if (selectedKeys.size === 0) return setError("No issues selected");

        setSubmitting(true);
        const res = await post<IMeeting>("/api/meeting/BatchAddJiraIssues", {
            meetingId: meeting.id,
            issueIds: Array.from(selectedKeys),
        });

        setSubmitting(false);
        if (res) {
            setMeeting(res);
            onClose();
        }
    }, [meeting, selectedKeys, post, setError, setMeeting, onClose]);

    return {
        missingJiraToken,
        sprints,
        sprintsLoading,
        selectedSprint,
        setSelectedSprint,
        searchResults,
        selectedKeys,
        searchLoading,
        submitting,
        toggleSelect,
        toggleSelectAll,
        submit,
    };
};

export default useBatchAddJira;
